import React from "react";
import { Box, Chip, Grid, Paper, Typography } from "@mui/material";
import FaceRetouchingNaturalIcon from "@mui/icons-material/FaceRetouchingNatural";
import AccessibilityNewOutlinedIcon from "@mui/icons-material/AccessibilityNewOutlined";
import RecordVoiceOverOutlinedIcon from "@mui/icons-material/RecordVoiceOverOutlined";

import palette from "../../../common/styles/palette";
import {
  AiAnalysis,
  AiFacetOrigin,
  AiVisualFacets,
} from "../../../services/sheai/SheAiChecklistTypes";
import { seekMainVideo } from "../debriefing-checklist/timestampLinks";
import { formatarMinutosSegundos as formatTime } from "./checklistMultimodalData";
import { cameraLabel } from "../../utils/videoSources";

type SoftSkillFacets = Pick<AiVisualFacets, "facial" | "body"> & {
  audio?: AiFacetOrigin & {
    vocal_tone?: string;
    active_listening?: string;
    communication?: string;
  };
};
type FacetType = keyof SoftSkillFacets;
type SpeechMoment = { start: number; end: number; texto: string };

interface FacetEvidence {
  id: string;
  type: FacetType;
  texts: string[];
  evidence?: string;
  limitation?: string;
}

export interface SoftSkillEvidenceMoment {
  id: string;
  sourceId: string;
  actor: string;
  start?: number;
  end?: number;
  speech?: string;
  facets: FacetEvidence[];
}

const FACET_TYPES: FacetType[] = ["facial", "body", "audio"];
const FACET_STYLES = {
  facial: {
    Icon: FaceRetouchingNaturalIcon,
    title: "Facial (Vídeo)",
    color: palette.brand,
    background: "rgba(139, 92, 246, 0.06)",
  },
  body: {
    Icon: AccessibilityNewOutlinedIcon,
    title: "Corporal (Vídeo)",
    color: palette.lilacMid,
    background: "rgba(167, 139, 250, 0.08)",
  },
  audio: {
    Icon: RecordVoiceOverOutlinedIcon,
    title: "Comunicação (Áudio)",
    color: "#2563EB",
    background: "rgba(59, 130, 246, 0.06)",
  },
};

const nonEmpty = (text?: string): text is string =>
  typeof text === "string" && !!text.trim();
const validTime = (time?: number): time is number =>
  typeof time === "number" && Number.isFinite(time) && time >= 0;
function facetTexts(type: FacetType, facets: SoftSkillFacets): string[] {
  switch (type) {
    case "facial":
      return [
        facets.facial?.expression,
        facets.facial?.eye_contact_pct != null
          ? `Contato visual estimado em ${facets.facial.eye_contact_pct}%`
          : undefined,
      ].filter(nonEmpty);
    case "body":
      return [
        facets.body?.posture,
        facets.body?.gestures,
        facets.body?.ergonomics,
      ].filter(nonEmpty);
    case "audio":
      return [
        facets.audio?.vocal_tone,
        facets.audio?.active_listening,
        facets.audio?.communication,
      ].filter(nonEmpty);
  }
}

/** Consulta usa apenas dados reais; a transcrição não cria facetas nem sincroniza câmeras. */
export function extractSoftSkillEvidence(
  analysis?: AiAnalysis | null,
  speechMoments: SpeechMoment[] = []
): SoftSkillEvidenceMoment[] {
  const primarySource =
    analysis?.video_sources?.find((source) => source.role === "primary")
      ?.source_id || "camera1";
  const references = new Map<
    string,
    Map<string, { facet: AiFacetOrigin; moment: SoftSkillEvidenceMoment }>
  >();
  for (const observation of analysis?.visual_observations || []) {
    for (const type of ["facial", "body"] as const) {
      const facet = observation.facets?.[type];
      if (facet) {
        const key = `${type}:${facet.observation_id || observation.id}`;
        const entries = references.get(key) || new Map();
        entries.set(observation.source_id, {
          facet,
          moment: {
            id: observation.id,
            sourceId: observation.source_id,
            actor: observation.actor,
            start: observation.start,
            end: observation.end,
            facets: [],
          },
        });
        references.set(key, entries);
      }
    }
  }

  const moments = new Map<string, SoftSkillEvidenceMoment>();
  const seen = new Set<string>();
  const speechByStart = new Map<number, SpeechMoment[]>();
  for (const speech of speechMoments) {
    const second = Math.floor(speech.start);
    const entries = speechByStart.get(second) || [];
    entries.push(speech);
    speechByStart.set(second, entries);
  }
  const addFacets = (
    facets: SoftSkillFacets,
    origin: SoftSkillEvidenceMoment,
    speech?: string,
    observationId?: string
  ) => {
    for (const type of FACET_TYPES) {
      const rawFacet = facets[type];
      if (!rawFacet) continue;
      const candidates = rawFacet.observation_id
        ? references.get(`${type}:${rawFacet.observation_id}`)
        : undefined;
      const matchingReference = rawFacet.source_id
        ? candidates?.get(rawFacet.source_id)
        : candidates?.size === 1
        ? Array.from(candidates.values())[0]
        : undefined;
      const facet = { ...matchingReference?.facet, ...rawFacet };
      const texts = facetTexts(type, { [type]: facet });
      if (!texts.length && !nonEmpty(facet.evidence)) continue;
      const sourceId =
        facet.source_id ||
        matchingReference?.moment.sourceId ||
        origin.sourceId;
      // Tempo da fala só é fallback na sua própria fonte, nunca na câmera focal.
      const localOrigin =
        matchingReference?.moment ||
        (sourceId === origin.sourceId ? origin : undefined);
      const start = validTime(facet.start) ? facet.start : localOrigin?.start;
      const end = validTime(facet.end) ? facet.end : localOrigin?.end;
      const actor = matchingReference?.moment.actor || origin.actor;
      const signature = JSON.stringify([
        type,
        sourceId,
        actor,
        start,
        end,
        texts,
        facet.evidence,
        facet.limitation,
      ]);
      const observation =
        facet.observation_id || matchingReference?.moment.id || observationId;
      const id = observation
        ? JSON.stringify([type, sourceId, observation])
        : signature;
      if (seen.has(id) || seen.has(signature)) continue;
      seen.add(id);
      seen.add(signature);

      const groupId = JSON.stringify([sourceId, actor, start, end]);
      const moment = moments.get(groupId) || {
        id: groupId,
        sourceId,
        actor,
        start,
        end,
        facets: [],
      };
      if (speech) moment.speech = speech;
      moment.facets.push({
        id,
        type,
        texts,
        evidence: facet.evidence,
        limitation: facet.limitation,
      });
      moments.set(groupId, moment);
    }
  };

  for (const moment of analysis?.timeline_multimodal || []) {
    const second = Math.floor(moment.start);
    const nearbySpeech = [second - 1, second, second + 1].flatMap(
      (key) => speechByStart.get(key) || []
    );
    const speech = nearbySpeech.find(
      (item) =>
        Math.abs(item.start - moment.start) < 0.75 &&
        Math.abs(item.end - moment.end) < 1.25
    )?.texto;
    addFacets(
      (moment.facets as SoftSkillFacets | undefined) || {},
      {
        id: String(moment.id),
        sourceId: primarySource,
        actor: moment.speaker,
        start: moment.start,
        end: moment.end,
        facets: [],
      },
      speech
    );
  }
  for (const observation of analysis?.visual_observations || []) {
    addFacets(
      observation.facets,
      {
        id: observation.id,
        sourceId: observation.source_id,
        actor: observation.actor,
        start: observation.start,
        end: observation.end,
        facets: [],
      },
      undefined,
      observation.id
    );
  }
  // Câmeras são ordenadas separadamente: seus tempos locais não provam simultaneidade.
  return Array.from(moments.values()).sort(
    (a, b) =>
      a.sourceId.localeCompare(b.sourceId) ||
      (validTime(a.start) ? a.start : Infinity) -
        (validTime(b.start) ? b.start : Infinity)
  );
}

export function matchesSoftSkillEvidence(
  moment: SoftSkillEvidenceMoment,
  query: string
): boolean {
  const term = query.trim().toLocaleLowerCase();
  return (
    !term ||
    [
      moment.actor,
      cameraLabel(moment.sourceId),
      moment.speech,
      ...moment.facets.flatMap((facet) => [
        FACET_STYLES[facet.type].title,
        ...facet.texts,
        facet.evidence,
        facet.limitation,
      ]),
    ].some((text) => nonEmpty(text) && text.toLocaleLowerCase().includes(term))
  );
}

export default function ChecklistSoftSkillsEvidence({
  moments,
}: {
  moments: SoftSkillEvidenceMoment[];
}) {
  return (
    <Paper
      elevation={0}
      sx={{
        maxHeight: 480,
        overflowY: "auto",
        p: 2,
        borderRadius: "10px",
        border: `1px solid ${palette.border}`,
        backgroundColor: "#FFFFFF",
      }}
    >
      <Typography variant="body2" sx={{ color: palette.textMuted, mb: 1.5 }}>
        Evidências de expressão facial, postura e comunicação, com o horário de
        cada câmera.
      </Typography>
      {!moments.length && (
        <Typography variant="body2" sx={{ color: palette.textMuted }}>
          Nenhuma evidência de soft skills disponível para este filtro.
        </Typography>
      )}
      {moments.map((moment) => {
        const start = validTime(moment.start)
          ? formatTime(moment.start)
          : undefined;
        const end =
          validTime(moment.end) &&
          validTime(moment.start) &&
          moment.end >= moment.start
            ? formatTime(moment.end)
            : undefined;
        const interval = start && `${start}${end ? ` - ${end}` : ""}`;
        const open = () => {
          if (start) seekMainVideo(start, moment.sourceId);
        };
        return (
          <Paper
            key={moment.id}
            elevation={0}
            role={start ? "button" : undefined}
            tabIndex={start ? 0 : undefined}
            aria-label={
              start ? `${cameraLabel(moment.sourceId)} ${interval}` : undefined
            }
            onClick={start ? open : undefined}
            onKeyDown={
              start
                ? (event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      open();
                    }
                  }
                : undefined
            }
            sx={{
              p: 2,
              mb: 2,
              border: `1px solid ${palette.border}`,
              borderRadius: "8px",
              boxShadow: palette.shadowSm,
              cursor: start ? "pointer" : "default",
              "&:hover, &:focus-visible": start
                ? { borderColor: palette.brand }
                : {},
            }}
          >
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 1.5 }}>
              <Chip
                label={moment.actor || "Participante não identificado"}
                size="small"
              />
              <Chip label={cameraLabel(moment.sourceId)} size="small" />
              <Chip
                label={interval ? `[▶ ${interval}]` : "Tempo não informado"}
                size="small"
                sx={{
                  backgroundColor: palette.brandWash,
                  color: palette.brand,
                }}
              />
            </Box>
            {moment.speech && (
              <Typography variant="body2" sx={{ color: palette.text, mb: 1.5 }}>
                Fala vinculada: {`"${moment.speech}"`}
              </Typography>
            )}
            <Grid container spacing={1.5}>
              {moment.facets.map((facet) => {
                const style = FACET_STYLES[facet.type];
                return (
                  <Grid
                    item
                    xs={12}
                    md={
                      moment.facets.length === 1
                        ? 12
                        : moment.facets.length === 2
                        ? 6
                        : 4
                    }
                    key={facet.id}
                  >
                    <Paper
                      elevation={0}
                      sx={{
                        p: 1.25,
                        height: "100%",
                        backgroundColor: style.background,
                        border: `1px solid ${style.color}33`,
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                          mb: 0.5,
                        }}
                      >
                        <style.Icon sx={{ fontSize: 16, color: style.color }} />
                        <Typography
                          variant="caption"
                          sx={{ fontWeight: 700, color: palette.brandDark }}
                        >
                          {style.title}
                        </Typography>
                      </Box>
                      {Array.from(
                        new Set(
                          [...facet.texts, facet.evidence].filter(nonEmpty)
                        )
                      ).map((text) => (
                        <Typography
                          key={text}
                          variant="caption"
                          sx={{ display: "block", color: palette.text }}
                        >
                          {text}
                        </Typography>
                      ))}
                      {facet.limitation && (
                        <Typography
                          variant="caption"
                          sx={{
                            display: "block",
                            color: palette.textMuted,
                            mt: 0.5,
                          }}
                        >
                          Limitação: {facet.limitation}
                        </Typography>
                      )}
                    </Paper>
                  </Grid>
                );
              })}
            </Grid>
          </Paper>
        );
      })}
    </Paper>
  );
}
